// Performance monitoring and optimization utilities
// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const payload = {
      message: String(message || 'unknown_error'),
      context: { location: typeof window !== 'undefined' ? (window.location?.pathname || '/') : '/', ...context },
      ts: Date.now(),
    };
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_) {}
};

class PerformanceMonitor {
  constructor() {
    this.metrics = {
      longTasks: [],
      layoutShifts: [],
      memoryUsage: [],
      renderTimes: []
    };
    this.observers = [];
    this.isMonitoring = false;
  }

  start() {
    if (this.isMonitoring) return;
    this.isMonitoring = true;

    // Monitor long tasks
    if ('PerformanceObserver' in window) {
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            this.metrics.longTasks.push({
              duration: entry.duration,
              startTime: entry.startTime,
              name: entry.name
            });
            
            // Log long tasks in development
            if (process.env.NODE_ENV === 'development' && entry.duration > 50) {
              // Long task detected - could be optimized
            }
          }
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });
        this.observers.push(longTaskObserver);
      } catch (e) {
        safeReportError('perf_longtask_monitor_unsupported');
      }

      // Monitor layout shifts
      try {
        const layoutShiftObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              this.metrics.layoutShifts.push({
                value: entry.value,
                sources: entry.sources
              });
            }
          }
        });
        layoutShiftObserver.observe({ entryTypes: ['layout-shift'] });
        this.observers.push(layoutShiftObserver);
      } catch (e) {
        safeReportError('perf_layout_shift_monitor_unsupported');
      }
    }

    // Monitor memory usage
    if ('memory' in performance) {
      setInterval(() => {
        this.metrics.memoryUsage.push({
          used: performance.memory.usedJSHeapSize,
          total: performance.memory.totalJSHeapSize,
          limit: performance.memory.jsHeapSizeLimit,
          timestamp: Date.now()
        });
      }, 5000);
    }
  }

  stop() {
    this.isMonitoring = false;
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }

  getMetrics() {
    return this.metrics;
  }

  getLongTasksCount() {
    return this.metrics.longTasks.length;
  }

  getAverageLongTaskDuration() {
    if (this.metrics.longTasks.length === 0) return 0;
    const total = this.metrics.longTasks.reduce((sum, task) => sum + task.duration, 0);
    return total / this.metrics.longTasks.length;
  }

  getLayoutShiftsCount() {
    return this.metrics.layoutShifts.length;
  }

  getCumulativeLayoutShift() {
    return this.metrics.layoutShifts.reduce((sum, shift) => sum + shift.value, 0);
  }
}

// Debounce utility for performance optimization
export function debounce(func, wait, immediate = false) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

// Throttle utility for performance optimization
export function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// Batch DOM updates to reduce layout thrashing
export function batchDOMUpdates(updates) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      updates.forEach(update => update());
      requestAnimationFrame(resolve);
    });
  });
}

// Optimize React re-renders
export function shouldComponentUpdate(prevProps, nextProps, keys = []) {
  if (keys.length === 0) {
    return JSON.stringify(prevProps) !== JSON.stringify(nextProps);
  }
  
  return keys.some(key => prevProps[key] !== nextProps[key]);
}

// Memory leak detection
export function detectMemoryLeaks() {
  if ('memory' in performance) {
    const memory = performance.memory;
    const usage = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
    
    if (usage > 0.8) {
      if (process.env.NODE_ENV === 'development') {
        // High memory usage detected in development
      } else {
        safeReportError('perf_high_memory_usage');
      }
    }
  }
}

// Optimize images loading
export function preloadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Optimize critical resources loading
export function preloadCriticalResources() {
  // In CRA dev, CSS is injected by JS; do not request /static/css/main.css
  // Only attempt safe preloads in production, and avoid hardcoded CSS filenames
  if (process.env.NODE_ENV !== 'production') {
    return;
  }
  const resources = ['/static/js/bundle.js'];
  resources.forEach((resource) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'script';
    link.href = resource;
    document.head.appendChild(link);
  });
}

// Monitor and optimize Firestore queries
export function optimizeFirestoreQueries() {
  // Track query performance
  const originalQuery = window.firebase?.firestore?.Query?.prototype?.get;
  
  if (originalQuery) {
    window.firebase.firestore.Query.prototype.get = function(...args) {
      const startTime = performance.now();
      return originalQuery.apply(this, args).then((result) => {
        const duration = performance.now() - startTime;
        if (duration > 100 && process.env.NODE_ENV === 'development') {
          // Slow Firestore query detected
        }
        return result;
      });
    };
  }
}

// Optimize socket connections
export function optimizeSocketConnections(socket) {
  if (!socket) return;

  // Debounce emit calls
  const originalEmit = socket.emit;
  socket.emit = debounce(originalEmit.bind(socket), 100);

  // Monitor connection performance
  socket.on('connect', () => {
    if (process.env.NODE_ENV === 'development') {
      // Socket connected
    }
  });

  socket.on('disconnect', () => {
    if (process.env.NODE_ENV === 'development') {
      // Socket disconnected
    }
  });
}

// --- Backwards-compat shims for existing imports ---
// Track core web vitals (LCP/FID/CLS) and forward to analytics if available
export function trackPerformance() {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (window.gtag && last) {
        window.gtag('event', 'LCP', {
          value: Math.round(last.startTime),
          event_category: 'Performance',
          non_interaction: true
        });
      }
    }).observe({ entryTypes: ['largest-contentful-paint'] });
  } catch {}

  try {
    new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const fid = entry.processingStart - entry.startTime;
        if (window.gtag) {
          window.gtag('event', 'FID', {
            value: Math.round(fid),
            event_category: 'Performance',
            non_interaction: true
          });
        }
      });
    }).observe({ entryTypes: ['first-input'] });
  } catch {}

  try {
    let cls = 0;
    new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (!entry.hadRecentInput) cls += entry.value;
      });
      if (window.gtag) {
        window.gtag('event', 'CLS', {
          value: Math.round(cls * 1000) / 1000,
          event_category: 'Performance',
          non_interaction: true
        });
      }
    }).observe({ entryTypes: ['layout-shift'] });
  } catch {}
}

// Initialize tracking helpers
export function initPerformanceTracking() {
  if (typeof window === 'undefined') return;
  trackPerformance();
  window.addEventListener('load', () => {
    const timing = performance.timing || {};
    const loadTime = (timing.loadEventEnd || 0) - (timing.navigationStart || 0);
    if (window.gtag && loadTime > 0) {
      window.gtag('event', 'page_load', {
        value: loadTime,
        event_category: 'Performance',
        non_interaction: true
      });
    }
  });
}

// Track product list loading time
export function trackProductLoad(startTime, productCount) {
  const loadTime = typeof performance !== 'undefined' ? performance.now() - startTime : 0;
  if (window && window.gtag) {
    window.gtag('event', 'product_load', {
      value: Math.round(loadTime),
      event_category: 'Performance',
      items: productCount,
      non_interaction: true
    });
  }
}

// Track image load timings
export function trackImageLoad(imageUrl, loadTime) {
  if (window && window.gtag) {
    window.gtag('event', 'image_load', {
      value: Math.round(loadTime),
      event_category: 'Performance',
      image: imageUrl,
      non_interaction: true
    });
  }
}

// Initialize performance monitoring
const performanceMonitor = new PerformanceMonitor();

// Start monitoring when the app loads
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    performanceMonitor.start();
    
    // Monitor memory usage periodically
    setInterval(detectMemoryLeaks, 30000);
    
    // Preload critical resources
    preloadCriticalResources();
    
    // Optimize Firestore queries
    optimizeFirestoreQueries();
  });
}

export { performanceMonitor };
export default performanceMonitor;
